import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useOnboarding } from "@/context/OnboardingContext";
import { validateUsername, trimAndSanitizeUsername } from '@/utils/validationUtils';
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { debugLogger } from '@/utils/debugLogger';
import { useStepValidation } from '@/hooks/useStepValidation';
import { OnboardingErrorModal } from './OnboardingErrorModal';
import { DateInput } from './DateInput';
import { calculateAge } from '@/utils/onboarding-utils';
import StickyOnboardingFooter from './StickyOnboardingFooter';

const UsernameStep = () => {
  const { data, updateData, nextStep, checkUsernameAvailability } = useOnboarding();
  const { validateAndSaveStep, isValidating } = useStepValidation();
  
  // Simplified state management
  const [localUsername, setLocalUsername] = useState('');
  const [birthdate, setBirthdate] = useState<string | null>(null);
  const [age, setAge] = useState<number>(0);
  const [validationStatus, setValidationStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [validationMessage, setValidationMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState<{
    message: string;
    timestamp: string;
    step: number;
    type: string;
  } | null>(null);

  // Validate existing username function
  const validateExistingUsername = async (username: string) => {
    setValidationStatus('validating');
    setValidationMessage('Validando...');
    
    // Passo 1: Validar regras locais
    const localValidation = validateUsername(username);
    if (!localValidation.isValid) {
      setValidationStatus('invalid');
      setValidationMessage(localValidation.error || 'Formato inválido');
      return;
    }
    
    // Passo 2: Verificar no Supabase
    try {
      const isAvailable = await checkUsernameAvailability(username);
      
      if (isAvailable) {
        setValidationStatus('valid');
        setValidationMessage('Username disponível');
      } else {
        setValidationStatus('invalid');
        setValidationMessage('Username já está em uso');
      }
    } catch (error) {
      setValidationStatus('invalid');
      setValidationMessage('Erro ao verificar disponibilidade');
    }
  };

  // Initialize username and birthdate, and auto-validate if present
  useEffect(() => {
    const initialUsername = data.username || '';
    
    // Auto-clear generated usernames (user_xxx pattern) on page load
    if (/^user_/i.test(initialUsername)) {
      setLocalUsername('');
      updateData({ username: '' });
      setValidationStatus('idle');
      setValidationMessage('');
      return;
    }
    
    // Populate fields
    setLocalUsername(initialUsername);
    setBirthdate(data.birthdate || null);
    setAge(data.age || 0);
    
    // Auto-validate existing username
    if (initialUsername.trim()) {
      validateExistingUsername(initialUsername);
    } else {
      setValidationStatus('idle');
      setValidationMessage('');
    }
  }, [data.username, data.birthdate, data.age, updateData]);

  const handleUsernameChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = trimAndSanitizeUsername(e.target.value);
    setLocalUsername(value);
    updateData({ username: value });
    
    // Se campo vazio, voltar ao estado inicial
    if (!value.trim()) {
      setValidationStatus('idle');
      setValidationMessage('');
      return;
    }
    
    // Validar o novo valor
    await validateExistingUsername(value);
  };

  const handleDateChange = (isoDate: string | null, calculatedAge: number) => {
    setBirthdate(isoDate);
    setAge(calculatedAge);
    updateData({ birthdate: isoDate }); // Only save birthdate, age is calculated dynamically
  };

  const handleContinue = async () => {
    if (validationStatus === 'valid') {
      await saveStep();
    }
  };

  const saveStep = async () => {
    const success = await validateAndSaveStep(
      { ...data, username: localUsername, birthdate, age },
      {
        stepNumber: 1,
        customValidation: (stepData) => {
          if (!stepData.username || stepData.username.trim() === '') {
            return { isValid: false, error: 'Um nome de usuário válido é obrigatório' };
          }
          if (!stepData.birthdate || !stepData.age || stepData.age < 18) {
            return { isValid: false, error: 'Você precisa ter pelo menos 18 anos' };
          }
          return { isValid: true };
        },
        onSuccess: () => {
          debugLogger.debug('Username step completed successfully');
          nextStep();
        },
        onError: (error, details) => {
          debugLogger.error('Username step failed:', error);
          
          const safeErrorDetails = {
            message: error || 'Erro desconhecido',
            timestamp: details?.timestamp || new Date().toISOString(),
            step: details?.step || 1,
            type: details?.type || 'username_save_failed'
          };
          
          setErrorDetails(safeErrorDetails);
          setShowErrorModal(true);
        }
      }
    );

    if (!success) {
      debugLogger.error('Username validation failed');
    }
  };

  const handleRetry = () => {
    setShowErrorModal(false);
    setErrorDetails(null);
    // Retry the save operation
    handleContinue();
  };

  const canProceed = () => {
    return validationStatus === 'valid' && !isValidating && birthdate && age >= 18;
  };

  const getStatusIcon = () => {
    if (validationStatus === 'validating' || isValidating) {
      return <Loader2 className="h-4 w-4 animate-spin text-white/70" />;
    }
    
    if (validationStatus === 'valid') {
      return <CheckCircle className="h-4 w-4 text-green-400" />;
    }
    
    if (validationStatus === 'invalid') {
      return <XCircle className="h-4 w-4 text-red-400" />;
    }
    
    return null;
  };

  const getStatusMessage = () => {
    return validationMessage;
  };

  const getMessageColor = () => {
    switch (validationStatus) {
      case 'validating':
        return 'text-white/70';
      case 'valid':
        return 'text-green-400';
      case 'invalid':
        return 'text-red-400';
      default:
        return 'text-white/70';
    }
  };

  return (
    <>
      <StickyOnboardingFooter
        buttonText="Continuar"
        onClick={handleContinue}
        disabled={!canProceed()}
        isLoading={isValidating}
        loadingText="Salvando..."
      >
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-4 md:mb-8">
            <h1 className="text-xl md:text-2xl font-bold text-white mb-1 md:mb-2">
              Escolha seu nome de usuário
            </h1>
            <p className="text-xs md:text-sm text-white/70">
              Este será o nome que outros usuários verão no seu perfil
            </p>
          </div>

          <Card className="bg-black/40 backdrop-blur-sm border-none shadow-2xl">
            <CardHeader className="text-center pb-4 md:pb-6">
              <CardTitle className="sr-only">
                Escolha seu nome de usuário
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 md:space-y-6 p-4 md:p-8">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-white font-medium text-sm">
                  Nome de usuário
                </Label>
                <div className="relative">
                  <Input
                    id="username"
                    name="username"
                    value={localUsername}
                    onChange={handleUsernameChange}
                    placeholder="Escolha um nome único..."
                    className="bg-black/30 border-white/20 text-white placeholder:text-white/50 pr-12 focus:border-luxury-pinkBright transition-colors"
                    maxLength={20}
                    disabled={isValidating}
                    data-testid="username-input"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {getStatusIcon()}
                  </div>
                </div>
                
                {getStatusMessage() && (
                  <p className={`text-xs md:text-sm mt-1 ${getMessageColor()}`}>
                    {getStatusMessage()}
                  </p>
                )}
                
                <div className="text-[10px] md:text-xs text-white/50 space-y-0.5 md:space-y-1">
                  <p>• Apenas letras, números, hífen (-) e underscore (_)</p>
                  <p>• Entre 5 e 20 caracteres</p>
                </div>
              </div>

              <div className="space-y-2">
                <DateInput
                  value={birthdate || ""}
                  onChange={handleDateChange}
                  label="Data de Nascimento"
                  data-testid="birthdate-input"
                />
                {birthdate && age < 18 && (
                  <p className="text-xs md:text-sm text-red-400">
                    Você precisa ter pelo menos 18 anos para se cadastrar
                  </p>
                )}
              </div>

              <div className="bg-white/5 border border-white/10 rounded-lg p-3 md:p-4">
                <div className="flex items-start gap-2 md:gap-3">
                  <div className="w-2 h-2 rounded-full bg-luxury-pinkBright mt-1.5 md:mt-2 flex-shrink-0" />
                  <div>
                    <h4 className="text-white font-medium mb-0.5 md:mb-1 text-sm">Dica</h4>
                    <p className="text-white/80 text-xs md:text-sm">
                      Escolha um nome único e memorável. Você não poderá alterá-lo depois.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </StickyOnboardingFooter>

      {showErrorModal && errorDetails && (
        <OnboardingErrorModal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          onRetry={handleRetry}
          stepNumber={1}
          errorDetails={errorDetails}
        />
      )}
    </>
  );
};

export default UsernameStep;
